export const textFieldError = (text, validation = {}) => {
    if (/<script|javascript:|\bon[a-z]+\s*=|<iframe|<object|<embed|<base|<form|vbscript:|data:text\/html/i.test(text))
        return 'Remove unsafe HTML or code from this field.';
    if (!validation.variables || !validation.urlSlugs)
        return undefined;
    const keys = [...text.matchAll(/\{\{(\w+)\}\}/g)].map((match) => match[1]);
    const unknown = keys.find((key) => !validation.variables.some((variable) => variable.key === key) &&
        !validation.urlSlugs.includes(key));
    if (unknown)
        return `Personalization {{${unknown}}} is unavailable. Remove it or choose an available detail.`;
    const unavailable = validation.variables.find((variable) => keys.includes(variable.key) &&
        variable.requires_target_kinds?.length &&
        validation.targetKind !== undefined &&
        !variable.requires_target_kinds.includes(validation.targetKind ?? ''));
    return unavailable
        ? `${unavailable.label} is unavailable for this audience. Choose another detail.`
        : undefined;
};
const validLink = (value, image, urlSlugs) => {
    const saved = /^\{\{(\w+)\}\}$/.exec(value);
    if (!image && saved)
        return urlSlugs === undefined || urlSlugs.includes(saved[1]);
    if (!value.startsWith('https://') && (image || !value.startsWith('mailto:')))
        return false;
    try {
        const parsed = new URL(value.replace(/\{\{\w+\}\}/g, 'placeholder'));
        return (value.trim() === value &&
            (parsed.protocol === 'https:' ? !!parsed.hostname : !!parsed.pathname));
    }
    catch {
        return false;
    }
};
export const CONTENT_CHOICES = {
    paragraph: {
        label: 'Text',
        description: 'Write a paragraph of your message.',
        category: 'Content'
    },
    heading: {
        label: 'Heading',
        description: 'Introduce a section with a clear title.',
        category: 'Content'
    },
    image: { label: 'Image', description: 'Add a photo or illustration.', category: 'Content' },
    button: { label: 'Button', description: 'Give readers a link to follow.', category: 'Content' },
    list: {
        label: 'List',
        description: 'Present bullet points or numbered steps.',
        category: 'Content'
    },
    testimonial: {
        label: 'Review',
        description: 'Include a quote and reviewer details.',
        category: 'Content'
    },
    two_column: {
        label: 'Two columns',
        description: 'Place content side by side.',
        category: 'Layout'
    },
    card: {
        label: 'Content card',
        description: 'Group related content in a panel.',
        category: 'Layout'
    },
    divider: {
        label: 'Separator',
        description: 'Separate sections with a horizontal line.',
        category: 'Layout'
    },
    spacer: {
        label: 'Space',
        description: 'Add breathing room between sections.',
        category: 'Layout'
    },
    conditional: {
        label: 'Conditional content',
        description: 'Show different content based on a recipient detail.',
        category: 'Advanced'
    }
};
export const EMPTY_CONTENT = {
    heading: { type: 'heading', text: '', level: 2 },
    paragraph: { type: 'paragraph', text: '' },
    button: { type: 'button', text: '', url: '' },
    image: { type: 'image', src: '' },
    list: { type: 'list', items: [''] },
    divider: { type: 'divider' },
    spacer: { type: 'spacer', height: 16 },
    two_column: { type: 'two_column', left: [], right: [] },
    card: { type: 'card', children: [] },
    testimonial: { type: 'testimonial', quote: '' },
    conditional: { type: 'conditional', variable: '', operator: 'eq', value: '', children: [] }
};
const requiredFieldErrors = (block) => {
    switch (block.type) {
        case 'paragraph':
            return block.text.trim() ? {} : { text: 'Write your message.' };
        case 'heading':
            return block.text.trim() ? {} : { text: 'Enter a heading.' };
        case 'button':
            return {
                ...(!block.text.trim() && { text: 'Enter a button label.' }),
                ...(!block.url.trim() && { url: 'Enter a destination link.' })
            };
        case 'image':
            return block.src.trim() ? {} : { src: 'Enter an image link.' };
        case 'testimonial':
            return block.quote.trim() ? {} : { quote: 'Enter a review quote.' };
        case 'list':
            return Object.fromEntries(block.items.flatMap((item, i) => item.trim() ? [] : [[`item-${i}`, 'Write this list item or remove it.']]));
        case 'conditional':
            return block.variable ? {} : { variable: 'Choose a recipient detail.' };
        default:
            return {};
    }
};
export const contentFieldErrors = (block, validation = {}) => {
    const errors = requiredFieldErrors(block);
    const fields = block.type === 'list'
        ? Object.fromEntries(block.items.map((item, index) => [`item-${index}`, item]))
        : Object.fromEntries(Object.entries(block).filter(([key, value]) => ['text', 'quote', 'source', 'byline', 'url', 'src', 'alt'].includes(key) && typeof value === 'string'));
    if (block.type === 'conditional' && block.variable)
        fields.variable = `{{${block.variable}}}`;
    Object.entries(fields).forEach(([key, value]) => {
        const error = textFieldError(value, validation);
        if (error && !errors[key])
            errors[key] = error;
    });
    if (block.type === 'button' && block.url && !validLink(block.url, false, validation.urlSlugs))
        errors.url = 'Use an HTTPS address, a mailto link, or choose a saved link.';
    if (block.type === 'image' && block.src && !validLink(block.src, true))
        errors.src = 'Enter a complete HTTPS image address, such as https://example.com/image.png.';
    return errors;
};
const contentBranches = (block) => block.type === 'two_column'
    ? [
        { label: 'Left column', blocks: block.left },
        { label: 'Right column', blocks: block.right }
    ]
    : block.type === 'card'
        ? [{ label: 'Card content', blocks: block.children }]
        : block.type === 'conditional'
            ? [
                { label: 'When the condition matches', blocks: block.children },
                { label: 'Otherwise', blocks: block.else_children ?? [] }
            ]
            : [];
export const contentChildren = (block) => contentBranches(block).flatMap((branch) => branch.blocks);
export const hasContentErrors = (block, validation) => Object.keys(contentFieldErrors(block, validation)).length > 0 ||
    contentChildren(block).some((child) => hasContentErrors(child, validation));
export const findContentErrors = (blocks, validation, location = 'Email content') => blocks.flatMap((block, index) => [
    ...Object.values(contentFieldErrors(block, validation)).map((message) => `${location} · ${CONTENT_CHOICES[block.type].label} ${index + 1}: ${message}`),
    ...contentBranches(block).flatMap((branch) => findContentErrors(branch.blocks, validation, `${location} · ${CONTENT_CHOICES[block.type].label} ${index + 1} · ${branch.label}`))
]);
